import React from 'react';
import PropTypes from 'prop-types';
import {
  Tag,
  Tooltip,
} from '@makeably/creativex-design-system';
import BoolIcon from 'components/atoms/BoolIcon';
import { getExplorerPath } from 'components/internal/explorer/shared';
import {
  areDefined,
  isDefined,
} from 'utilities/item';
import { internalVersionsPath } from 'utilities/routes';
import {
  splitSnakeCase,
  titleize,
  toDate,
  toDateTime,
} from 'utilities/string';

// NOTE: These are deprecated. Use the exports in utilities/item.js instead
export const NULL_LABEL = 'N/A';
export const NULL_VALUE = '~';
export const NULL_DIMENSION = {
  label: NULL_LABEL,
  value: NULL_VALUE,
};

export const versionTypes = [
  'AdAccount',
  'AdCampaign',
  'BmeDateRange',
  'ChannelConfig',
  'ClassifierSubscription',
  'Contract',
  'FacebookAdAccount',
  'FacebookAdCampaign',
  'FacebookAdPage',
  'Guideline',
  'GuidelineCheck',
  'GuidelineDetail',
  'GuidelineWeight',
  'Organization',
  'OrganizationAffiliate',
  'OrganizationAttribute',
  'OrganizationBrand',
  'OrganizationFeature',
  'Partner',
  'ProductFeature',
  'Report',
  'Score',
  'ScoreVersion',
  'Tier',
  'TierCollection',
  'TierCollectionVersion',
  'User',
];

const versionTypeSet = new Set(versionTypes);

export const optionProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
});

export const optionArrayProps = PropTypes.arrayOf(optionProps);

export const filterProps = PropTypes.objectOf(
  PropTypes.arrayOf(optionProps),
);

export const emptyState = (
  <div className="t-empty u-flexRow u-justifyCenter u-marginTop-16">
    No data to display
  </div>
);

export function getBoolDimension(value, useIcon = false, withLabel = false) {
  if (!isDefined(value)) return NULL_DIMENSION;

  const label = value ? 'True' : 'False';
  let element;
  const boolIcon = <BoolIcon value={value} />;
  if (useIcon) {
    if (withLabel) {
      element = (
        <div className="u-flexRow u-gap-8">
          { boolIcon }
          { label }
        </div>
      );
    } else if (useIcon) {
      element = boolIcon;
    }
  }

  return {
    element,
    label,
    value,
  };
}

export function getDateDimension(value) {
  if (!isDefined(value)) return NULL_DIMENSION;

  return {
    label: toDate(new Date(value)),
    value,
  };
}

export function getDateTimeDimension(value) {
  if (!isDefined(value)) return NULL_DIMENSION;

  return {
    label: toDateTime(new Date(value)),
    value,
  };
}

export function getLink(url, value, useNewTab = false) {
  const props = useNewTab ? {
    rel: 'noreferrer',
    target: '_blank',
  } : {};

  return (
    <a href={url} {...props}>
      { value }
    </a>
  );
}

export function getLinkDimension(value, url, useNewTab = false) {
  if (!isDefined(value)) return NULL_DIMENSION;

  return {
    element: getLink(url, value, useNewTab),
    value,
  };
}

export function getExplorerLinkDimension(type, id, initialLabel, useNewTab = false) {
  if (!type || !id) return NULL_DIMENSION;

  const label = initialLabel ?? id;
  const url = getExplorerPath(type, id);

  if (!url) return { value: label };

  return getLinkDimension(label, url, useNewTab);
}

export function getNullableDimension(value) {
  if (!isDefined(value)) return NULL_DIMENSION;

  return { value };
}

export function getPathsDimension(values, path, useNewTab = false) {
  if (!areDefined(values)) return NULL_DIMENSION;

  const props = useNewTab ? {
    rel: 'noreferrer',
    target: '_blank',
  } : {};

  return {
    element: (
      <span className="u-flexRow u-gap-8">
        { values.map((value) => (
          <a key={value} href={path(value)} {...props}>
            { value }
          </a>
        )) }
      </span>
    ),
    value: values.map((value) => `${value}`).join('::'),
  };
}

export function getStatusDimension(value) {
  const values = Array.isArray(value) ? value : [value];

  if (!areDefined(values)) return NULL_DIMENSION;

  const labels = values.map((v) => titleize(splitSnakeCase(v)));

  return {
    label: labels.join(', '),
    value: values.join('::'),
  };
}

export function getUniqueArrayDimension(values) {
  const set = new Set(values);

  if (set.size === 0) {
    return { value: NULL_LABEL };
  } else if (set.size === 1) {
    return { value: Array.from(set)[0] };
  }
  return {
    label: 'Multiple',
    value: Array.from(set).join(', '),
  };
}

export function getVersionLink(type, id, useNewTab = false, label = 'Changes') {
  if (!versionTypeSet.has(type)) return null;

  const url = internalVersionsPath({
    item_id: id,
    type,
  });

  return getLink(url, label, useNewTab);
}

export function getVersionLinkDimension(type, id, useNewTab, label) {
  const link = getVersionLink(type, id, label);

  if (!link) return NULL_DIMENSION;

  return {
    element: link,
    value: id,
  };
}

export function renderBetaTag() {
  return (
    <Tooltip label="If this feature has been useful to you, or if you have new use cases, please slack @Phil">
      <Tag color="blue" label="Beta" />
    </Tooltip>
  );
}
